import { Fragment, useState } from "react";
import { ChevronRight } from "lucide-react";
import { fmtNum } from "../lib/format";
import { fmtClock, fmtDur, fmtHours, dayLabel } from "../lib/pickerSheet";

const DayRow = ({ d }) => (
  <tr className="bg-[#0B0F19]/60 border-b border-slate-800/40 text-xs" data-testid={`day-row-${d.day}`}>
    <td className="pl-12 py-2 text-slate-400 whitespace-nowrap">{dayLabel(d.day)}</td>
    <td className="px-3 font-mono text-slate-300">{fmtNum(d.picks)}</td>
    <td className="px-3 font-mono text-slate-400 whitespace-nowrap"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 mr-2" data-testid={`day-shift-${d.day}`}>{d.shift}</span>{fmtClock(d.first)} – {fmtClock(d.last)}{d.ot > 0 && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-300" data-testid={`day-ot-${d.day}`} title={`Shift ${fmtClock((d.shiftStart + 86400) % 86400)}–${fmtClock(d.shiftEnd % 86400)}`}>OT +{fmtDur(d.ot)}</span>}</td>
    <td className="px-3 font-mono text-slate-300">{fmtHours(d.worked)}</td>
    <td className="px-3 font-mono text-slate-400">{fmtNum(d.pph)}</td>
    <td className="px-3 font-mono text-amber-300/80">{d.late ? fmtDur(d.late) : "—"}</td>
    <td className="px-3 font-mono text-amber-300/80">{d.early ? fmtDur(d.early) : "—"}</td>
    <td className="px-3 py-2">
      <div className="flex flex-wrap gap-1">
        {d.gaps.length === 0 && <span className="text-slate-600">—</span>}
        {d.gaps.map((g, i) => (
          <span key={i} className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 whitespace-nowrap" title={`Idle ${fmtClock(g.start)} → ${fmtClock(g.start + g.dur)}`}>
            {fmtClock(g.start)} · {fmtDur(g.dur)}
          </span>
        ))}
      </div>
    </td>
    <td className="px-3 font-mono text-rose-300">{fmtDur(d.lost)}</td>
    <td className="px-3 font-mono text-cyan-300">{d.ot ? fmtDur(d.ot) : "—"}</td>
  </tr>
);

const HEAD = ["Week commencing", "Picks", "Days · Shift / span", "Hours", "Picks / hr", "Late start", "Early finish", "Large gaps", "Lost time", "Overtime"];

export const WeeklyTable = ({ weeks, expandAll }) => {
  const [open, setOpen] = useState({});
  return (
    <div className="panel overflow-x-auto" data-testid="weekly-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-800">{HEAD.map((h) => <th key={h} className="text-left px-3 py-3 font-mono text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap first:pl-5">{h}</th>)}</tr></thead>
        <tbody>
          {[...weeks].reverse().map((w) => (
            <Fragment key={w.week}>
              <tr data-testid={`week-row-${w.week}`} onClick={() => setOpen({ ...open, [w.week]: !open[w.week] })} className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer transition-colors">
                <td className="pl-5 py-3 text-slate-100 whitespace-nowrap"><ChevronRight className={`inline h-3.5 w-3.5 mr-2 text-slate-500 transition-transform ${open[w.week] ? "rotate-90" : ""}`} />{dayLabel(w.week)}</td>
                <td className="px-3 font-mono text-slate-200">{fmtNum(w.picks)}</td>
                <td className="px-3 font-mono text-slate-400">{w.days.length} days</td>
                <td className="px-3 font-mono text-slate-200">{fmtHours(w.worked)}</td>
                <td className="px-3 font-mono text-slate-300">{fmtNum(w.pph)}</td>
                <td className="px-3 font-mono text-amber-300">{fmtDur(w.late)}</td>
                <td className="px-3 font-mono text-amber-300">{fmtDur(w.early)}</td>
                <td className="px-3 font-mono text-rose-300/80">{w.gapCount} · {fmtDur(w.gapTime)}</td>
                <td className="px-3 font-mono text-rose-300 font-medium">{fmtDur(w.lost)}</td>
                <td className="px-3 font-mono text-cyan-300">{w.otDays ? `${w.otDays}d · ${fmtDur(w.ot)}` : "—"}</td>
              </tr>
              {(expandAll || open[w.week]) && w.days.map((d) => <DayRow key={d.day} d={d} />)}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
